import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Signin.css';

function Signin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { setUserData, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect URL from query params or from referrer
  const params = new URLSearchParams(location.search);
  const rawRedirectUrl = params.get('redirect') || document.referrer || '/profile';
  const sessionExpired = params.get('session_expired') === 'true';
  const passwordReset = params.get('password_reset') === 'true';
  
  // Clean the redirect URL to prevent redirection loops and nested auth pages
  const redirectUrl = useMemo(() => {
    // Parse the URL to see if it's an internal URL
    let cleanUrl = rawRedirectUrl;
    let fragment = '';
    
    try {
      // If it's a full URL, extract just the path and fragment
      if (rawRedirectUrl.startsWith('http')) {
        const urlObj = new URL(rawRedirectUrl);
        cleanUrl = urlObj.pathname + urlObj.search;
        fragment = urlObj.hash;
      } else {
        // Handle local paths with fragments
        const hashIndex = cleanUrl.indexOf('#');
        if (hashIndex !== -1) {
          fragment = cleanUrl.substring(hashIndex);
          cleanUrl = cleanUrl.substring(0, hashIndex);
        }
      }
      
      // Don't redirect back to auth pages
      const authPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
      for (const authPage of authPages) {
        if (cleanUrl.startsWith(authPage)) {
          return '/';  // Redirect to home instead
        }
      }
      
      // Return the cleaned path with the fragment if it exists
      return cleanUrl + fragment;
    } catch (e) {
      console.error('Error parsing redirect URL:', e);
      return '/';  // Default to home on error
    }
  }, [rawRedirectUrl]);

  // Set session expired error message
  useEffect(() => {
    if (sessionExpired) {
      setError('Your session has expired. Please sign in again.');
    }
  }, [sessionExpired]);

  // Redirect if already signed in
  useEffect(() => {
    if (currentUser) {
      navigate(redirectUrl !== '/signin' ? decodeURIComponent(redirectUrl) : '/profile');
    }
  }, [currentUser, navigate, redirectUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      const data = await login(email, password);
      
      // Update auth context with user data
      setUserData(data, data.access_token);
      
      // Check if user is verified
      if (!data.is_verified) {
        // If user is not verified, redirect to verification page
        // We also pass the original redirect URL so the user can continue their journey after verification
        navigate(`/verify-email?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirectUrl)}`);
        return;
      }
      
      // User is verified, proceed with normal redirect
      const decodedRedirectUrl = decodeURIComponent(redirectUrl);
      navigate(decodedRedirectUrl !== '/signin' ? decodedRedirectUrl : '/profile');
    } catch (err) {
      console.error('Login error:', err);
      
      // Use more specific error messages when available
      if (err.isCredentialsError) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (err.isUserNotFound) {
        setError('No account found with this email. Please check your email or sign up.');
      } else if (sessionExpired) {
        setError('Your session has expired. Please sign in again.');
      } else {
        setError(err.detail || 'Failed to sign in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Sign In</h2>
        {passwordReset && (
          <div className="alert alert-success">
            Password reset successful! You can now sign in with your new password.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="auth-links">
          <Link to="/forgot-password">Forgot Password?</Link>
          <span className="separator">•</span>
          <Link to={`/signup${location.search}`}>Need an account? Sign up</Link>
        </div>
      </div>
    </div>
  );
}

export default Signin;